import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["triggerType", "triggerValue", "triggerHint", "intentSelect", "actionType", "actionValue", "actionHint", "autonomy", "autonomyHint"]

  connect() {
    this.updateTriggerHint()
    this.updateActionHint()
    this.updateAutonomyOptions()
    this.maybeShowIntentSelect()
  }

  updateTriggerHint() {
    const triggerType = this.triggerTypeTarget.value

    const hints = {
      sender: {
        placeholder: "e.g., john@example.com",
        hint: "Enter the full email address of the sender you want to watch for. Partial matches are supported (e.g., 'john' will match john@example.com)."
      },
      keyword: {
        placeholder: "e.g., urgent, invoice, contract",
        hint: "Enter a keyword or phrase to search for in the email subject or body. Matching is case-insensitive."
      },
      domain: {
        placeholder: "e.g., example.com",
        hint: "Enter the domain name (without @) to match all emails from that domain. E.g., 'example.com' matches anyone@example.com."
      },
      intent: {
        placeholder: "",
        hint: "Select the email intent that should trigger this automation. Intents are automatically detected by AI."
      }
    }

    const config = hints[triggerType] || hints.sender
    this.triggerValueTarget.placeholder = config.placeholder
    this.triggerHintTarget.textContent = config.hint
    this.maybeShowIntentSelect()
  }

  maybeShowIntentSelect() {
    if (!this.hasIntentSelectTarget || !this.hasTriggerValueTarget) return

    const triggerType = this.triggerTypeTarget.value

    if (triggerType === 'intent') {
      // Show select, hide text input
      this.triggerValueTarget.classList.add('d-none')
      this.intentSelectTarget.classList.remove('d-none')
      this.intentSelectTarget.setAttribute('name', 'automation[trigger_value]')
      this.triggerValueTarget.removeAttribute('name')
    } else {
      // Show text input, hide select
      this.triggerValueTarget.classList.remove('d-none')
      this.intentSelectTarget.classList.add('d-none')
      this.triggerValueTarget.setAttribute('name', 'automation[trigger_value]')
      this.intentSelectTarget.removeAttribute('name')
    }
  }

  updateActionHint() {
    const actionType = this.actionTypeTarget.value

    const hints = {
      sms: {
        placeholder: "e.g., +14155551234",
        hint: "Enter phone number in E.164 format: + followed by country code and number with no spaces or dashes. US example: +14155551234"
      },
      slack: {
        placeholder: "e.g., https://hooks.slack.com/services/...",
        hint: "Enter your Slack webhook URL. Get it from: Slack → Apps → Incoming Webhooks → Add New Webhook. The URL should start with https://hooks.slack.com/"
      },
      email: {
        placeholder: "e.g., team@company.com",
        hint: "Enter the destination email address where you want to forward matching emails."
      }
    }

    const config = hints[actionType] || hints.email
    this.actionValueTarget.placeholder = config.placeholder
    this.actionHintTarget.textContent = config.hint

    // Action class governs which autonomy options make sense.
    this.updateAutonomyOptions()
  }

  isQuietAction() {
    const action = this.hasActionTypeTarget ? this.actionTypeTarget.value : ""
    return action === "archive" || action === "label"
  }

  // Quiet actions (archive/label) offer batch-review vs. silent (reported in
  // digest); send-class actions offer ask-first vs. the 2-minute undo — they
  // can't run fully automatically. Rebuilt whenever the action changes.
  updateAutonomyOptions() {
    if (!this.hasAutonomyTarget) return

    const quiet = this.isQuietAction()
    const options = quiet
      ? [["propose", "Review in batch"], ["full_auto", "Automatic — reported in digest"]]
      : [["propose", "Ask first (propose)"], ["undo_window", "Run with a 2-minute undo"]]

    const select = this.autonomyTarget
    const previous = select.value
    const allowed = options.map(([value]) => value)

    select.innerHTML = ""
    for (const [value, label] of options) {
      const option = document.createElement("option")
      option.value = value
      option.textContent = label
      select.appendChild(option)
    }

    // Keep the prior choice if still valid; otherwise default (quiet → silent,
    // send-class → ask first).
    select.value = allowed.includes(previous) ? previous : (quiet ? "full_auto" : "propose")

    if (this.hasAutonomyHintTarget) {
      this.autonomyHintTarget.textContent = quiet
        ? "Quiet actions run without per-email cards. “Automatic” reports runs in your digest; “Review in batch” collects them on one Tidy card."
        : "Send-class actions (SMS, Slack, email, webhook) can't run fully automatically."
    }
  }
}

